import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

const readSaved = (key) => {
  try {
    return Number(window.localStorage.getItem(key)) || 0;
  } catch {
    return 0;
  }
};

const save = (key, value) => {
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // storage unavailable (private mode): the address still carries the size
  }
};

// Keeps a table's page, page size and search in the address
// (?page=3&size=100&q=5906), so Back from a detail page returns to the same
// view. The last page size picked is remembered as the default.
export default function useTableUrlState(tableKey, defaultSize = 10) {
  const [params, setParams] = useSearchParams();
  const sizeKey = `table-page-size:${tableKey}`;

  const page = Math.max(1, Number(params.get("page")) || 1);
  const pageSize = Number(params.get("size")) || readSaved(sizeKey) || defaultSize;
  const q = params.get("q") || "";

  const update = useCallback(
    (changes) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(changes).forEach(([key, value]) => {
            if (value === "" || value == null || (key === "page" && value === 1)) {
              next.delete(key);
            } else {
              next.set(key, String(value));
            }
          });
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const setPagination = useCallback(
    (current, size) => {
      if (size !== pageSize) {
        save(sizeKey, size);
        update({ page: 1, size });
      } else {
        update({ page: current, size });
      }
    },
    [pageSize, sizeKey, update],
  );

  const setQuery = useCallback(
    (value) => update({ q: value.trim(), page: 1 }),
    [update],
  );

  return { page, pageSize, q, setPagination, setQuery };
}
